import { useCallback, useEffect, useState } from 'react';
import {
  helpdeskApi,
  searchRondoUsers,
  type HelpdeskTicketDetailDto,
  type HelpdeskMessageDto,
  type HelpdeskAssignees,
  type HelpdeskTagDto,
  type RondoUserHit,
} from '../api/helpdesk';
import { useAuth } from '../auth/AuthContext';
import { HD_STATUS } from './HelpdeskPage';
import { messageBodyText } from './message-format';
import { renderMessageHtml } from './message-html';
import { ticketCodeLabel } from './ticket-code';
import { activityText } from './activity-format';
import { AddContactToCard } from './AddContactToCard';
import { confirmDialog } from '../shell/dialogs';
import { isRiskyAttachment } from './risky-attachment';
import { Button } from '../components/arc/button/button';
import { CompactSelect } from '../components/arc/compact-select/compact-select';
import { ArcPanel, ArcTabs } from '../components/arc/primitives/arc-primitives';
import './helpdesk-detail-arc.css';

interface Props {
  ticketId: string;
  onClose: () => void;
  onChanged?: () => void;
}

const STATUS_ORDER = ['novy', 'otevreny', 'ceka_na_uziv', 'vyreseno', 'zavreno', 'spam'];

const DETAIL_REFRESH_MS = 20000;

function fmtDateTime(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleString('cs-CZ');
}

function errText(e: unknown): string {
  const code = (e as { code?: string })?.code;
  if (code === 'MODULE_DENIED') return 'Nemáš oprávnění pro tuto akci.';
  if (code === 'REPLY_SEND_FAILED') return 'Odpověď se nepodařilo odeslat, do vlákna se neuložila. Zkus to prosím znovu.';
  return code ? `Chyba: ${code}` : e instanceof Error ? e.message : 'Chyba';
}

export function HelpdeskDetail({ ticketId, onClose, onChanged }: Props) {
  const { me } = useAuth();
  const isRondoContext = me?.activeSpaceSlug === 'rondo';
  const [ticket, setTicket] = useState<HelpdeskTicketDetailDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [assignees, setAssignees] = useState<HelpdeskAssignees>({ users: [], aiAgents: [] });
  const [allTags, setAllTags] = useState<HelpdeskTagDto[]>([]);

  const [composerTab, setComposerTab] = useState<'reply' | 'copilot' | 'note'>('reply');
  const [replyText, setReplyText] = useState('');
  const [noteText, setNoteText] = useState('');
  const [busy, setBusy] = useState(false);
  const [activityOpen, setActivityOpen] = useState(false);
  const [actErr, setActErr] = useState('');

  const reload = useCallback(() => {
    return helpdeskApi.get(ticketId)
      .then((t) => { setTicket(t); setError(null); })
      .catch((e) => setError(errText(e)));
  }, [ticketId]);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    helpdeskApi.get(ticketId)
      .then((t) => { if (alive) { setTicket(t); setError(null); } })
      .catch((e) => { if (alive) setError(errText(e)); })
      .finally(() => { if (alive) setLoading(false); });
    helpdeskApi.assignees().then((a) => alive && setAssignees(a)).catch(() => {});
    helpdeskApi.tags().then((t) => alive && setAllTags(t)).catch(() => {});
    return () => { alive = false; };
  }, [ticketId]);

  useEffect(() => {
    document.body.classList.add('hd-side-open');
    return () => document.body.classList.remove('hd-side-open');
  }, []);

  // Pull new messages into the open thread without the operator reloading.
  // Skipped while an action is in flight or the tab is hidden; a draft in the
  // composer is local state, so a refresh never discards it.
  useEffect(() => {
    const tick = () => { if (!document.hidden && !busy) void reload(); };
    const id = window.setInterval(tick, DETAIL_REFRESH_MS);
    const onVisible = () => { if (!document.hidden) tick(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.clearInterval(id);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [reload, busy]);

  const act = useCallback(
    async (fn: () => Promise<unknown>) => {
      setBusy(true); setActErr('');
      try { await fn(); await reload(); onChanged?.(); }
      catch (e) { setActErr(errText(e)); }
      finally { setBusy(false); }
    },
    [reload, onChanged],
  );

  const st = ticket ? HD_STATUS[ticket.status] ?? { label: ticket.status, color: '#6c6c70' } : null;

  const assigneeValue = ticket
    ? ticket.assigneeType === 'ai'
      ? `ai:${ticket.assigneeAiAgentId}`
      : ticket.assigneeUserId
        ? `user:${ticket.assigneeUserId}`
        : ''
    : '';

  const onAssigneeChange = (v: string) => {
    if (!ticket) return;
    if (!v) return act(() => helpdeskApi.setAssignee(ticket.id, null));
    const [type, id] = v.split(':');
    if (!id) return;
    return act(() =>
      helpdeskApi.setAssignee(ticket.id, type === 'ai' ? { type: 'ai', aiAgentId: id } : { type: 'user', userId: id }),
    );
  };

  const sendReply = (setStatus?: string) => {
    if (!ticket || !replyText.trim()) return;
    return act(async () => { await helpdeskApi.reply(ticket.id, replyText.trim(), setStatus); setReplyText(''); });
  };
  const saveNote = () => {
    if (!ticket || !noteText.trim()) return;
    return act(async () => { await helpdeskApi.note(ticket.id, noteText.trim()); setNoteText(''); });
  };

  const ticketTagIds = new Set((ticket?.tags ?? []).map((t) => t.id));

  return (
    <>
      <div className="hd-side-backdrop" onClick={onClose} />
      <aside className="hd-side-panel hd-arc-detail p1-arc-scope" role="dialog" aria-label="Detail ticketu">
        <header className="hd-side-head">
          <Button type="button" variant="ghost" size="sm" className="hd-side-close" onClick={(event) => { event.stopPropagation(); onClose(); }} aria-label="Zavřít">×</Button>
          <span className="hd-side-title">{ticket ? ticketCodeLabel(ticket) : ticketId}</span>
          {ticket && (
            <Button
              type="button"
              variant="danger"
              size="sm"
              className="hd-side-archive"
              disabled={busy}
              onClick={() => { void confirmDialog({ title: 'Archivovat ticket?', confirmLabel: 'Archivovat' }).then((ok) => { if (ok) void act(() => helpdeskApi.archive(ticket.id)).then(onClose); }); }}
            >
              Archivovat
            </Button>
          )}
        </header>

        {loading && <p className="hd-empty">Načítám…</p>}
        {error && <p className="hd-error">{error}</p>}

        {ticket && st && (
          <div className="hd-detail-grid">
            <div className="hd-detail-main">
              <div className="hd-badges">
                <span className="hd-status-badge" style={{ color: st.color, background: `${st.color}1f` }}>{st.label}</span>
                {ticket.urgent && <span className="hd-badge-urgent">Urgentní</span>}
                {ticket.aiHandled && <span className="hd-badge-ai">✓ Vyřízeno AI</span>}
                <span className="hd-account-chip">{ticket.accountName} · {ticket.accountEmail}</span>
              </div>

              <h4 className="hd-detail-subject">{ticketCodeLabel(ticket)} - {ticket.subject}</h4>

              <Conversation
                ticket={ticket}
                busy={busy}
                onArchive={(mid) => act(() => helpdeskApi.deleteMessage(ticket.id, mid))}
              />

              <ArcPanel className="hd-composer">
                <ArcTabs className="hd-reply-tabs" items={[
                  { value: 'reply', label: <><span className="hd-tab-long">Odpověď zákazníkovi</span><span className="hd-tab-short">Odpověď</span></> },
                  { value: 'copilot', label: <><span className="hd-tab-long">Copilot návrh</span><span className="hd-tab-short">Copilot</span></> },
                  { value: 'note', label: <><span className="hd-tab-long">Interní poznámka</span><span className="hd-tab-short">Poznámka</span></> },
                ]} value={composerTab} onChange={setComposerTab} label="Způsob odpovědi" />

                {composerTab === 'copilot' ? (
                  <div className="hd-copilot-pane">
                    <div className="hd-copilot-empty">
                      <span className="hd-copilot-emoji">AI</span>
                      <div>
                        <strong>Copilot návrh</strong>
                        <p>AI připraví návrh odpovědi, který schválíš nebo upravíš. Generování se zapne se zapojením AI vrstvy (samostatný krok).</p>
                      </div>
                    </div>
                    <Button type="button" variant="primary" size="sm" disabled title="Připravujeme (AI vrstva)">Vygenerovat návrh</Button>
                  </div>
                ) : composerTab === 'reply' ? (
                  <>
                    <textarea
                      className="hd-reply-box"
                      rows={6}
                      placeholder="Napiš odpověď…"
                      value={replyText}
                      onChange={(e) => setReplyText(e.target.value)}
                    />
                    <div className="hd-send-row">
                      <Button type="button" variant="primary" size="sm" disabled={busy || !replyText.trim()} onClick={() => sendReply()}>Odeslat</Button>
                      <Button type="button" variant="secondary" size="sm" disabled={busy || !replyText.trim()} onClick={() => sendReply('vyreseno')}>Odeslat a vyřešit</Button>
                      <Button type="button" variant="secondary" size="sm" disabled={busy || !replyText.trim()} onClick={() => sendReply('zavreno')}>Odeslat a zavřít</Button>
                    </div>
                  </>
                ) : (
                  <>
                    <textarea
                      className="hd-reply-box"
                      rows={7}
                      placeholder="Poznámka jen pro operátory…"
                      value={noteText}
                      onChange={(e) => setNoteText(e.target.value)}
                    />
                    <div className="hd-send-row">
                      <Button type="button" variant="primary" size="sm" disabled={busy || !noteText.trim()} onClick={saveNote}>Uložit poznámku</Button>
                    </div>
                  </>
                )}
              </ArcPanel>

              {actErr && <p className="hd-error">{actErr}</p>}

              {ticket.activity.length > 0 && (
                <div className="hd-activity">
                  <button
                    type="button"
                    className="hd-activity-toggle"
                    aria-expanded={activityOpen}
                    onClick={() => setActivityOpen((v) => !v)}
                  >
                    <span className="hd-activity-caret">{activityOpen ? '▾' : '▸'}</span>
                    <span>{activityOpen ? 'Skrýt aktivitu' : 'Zobrazit aktivitu'}</span>
                    <span className="hd-activity-count">{ticket.activity.length}</span>
                  </button>
                  {activityOpen && (
                    <div className="hd-activity-list">
                      {ticket.activity.map((a) => (
                        <div key={a.id} className="hd-activity-row">
                          <span className="hd-activity-dot" />
                          <span>{activityText(a)}</span>
                          <span className="hd-activity-time">{fmtDateTime(a.createdAt)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            <aside className="hd-side-tiles">
              <ArcPanel className="hd-side-tile">
                <span className="hd-tile-label">Stav</span>
                <CompactSelect className="hd-arc-select" value={ticket.status} disabled={busy} ariaLabel="Stav ticketu" onChange={(next) => act(() => helpdeskApi.setStatus(ticket.id, next))} options={STATUS_ORDER.map((s) => ({ value: s, label: HD_STATUS[s]?.label ?? s }))} />
              </ArcPanel>
              <ArcPanel className="hd-side-tile">
                <span className="hd-tile-label">Přiřazeno</span>
                <select value={assigneeValue} disabled={busy} onChange={(e) => onAssigneeChange(e.target.value)}>
                  <option value="">- nepřiřazeno -</option>
                  {assignees.users.length > 0 && (
                    <optgroup label="Lidé">
                      {assignees.users.map((u) => <option key={`u${u.id}`} value={`user:${u.id}`}>{u.name}</option>)}
                    </optgroup>
                  )}
                  {assignees.aiAgents.length > 0 && (
                    <optgroup label="AI">
                      {assignees.aiAgents.map((a) => <option key={`a${a.id}`} value={`ai:${a.id}`}>{a.name}</option>)}
                    </optgroup>
                  )}
                </select>
              </ArcPanel>
              <ArcPanel className="hd-side-tile">
                <span className="hd-tile-label">Urgentní</span>
                <label className="hd-switch">
                  <input type="checkbox" checked={!!ticket.urgent} disabled={busy} onChange={(e) => act(() => helpdeskApi.setUrgent(ticket.id, e.target.checked))} />
                  {ticket.urgent ? 'Ano' : 'Ne'}
                </label>
              </ArcPanel>
              <ArcPanel className="hd-side-tile">
                <span className="hd-tile-label">Tagy</span>
                <span className="hd-tag-row">
                  {ticket.tags.map((tag) => (
                    <button
                      key={tag.id}
                      type="button"
                      className="hd-tag-chip hd-tag-removable"
                      style={{ background: `${tag.color}20`, color: tag.color, borderColor: tag.color }}
                      title="Odebrat tag"
                      disabled={busy}
                      onClick={() => act(() => helpdeskApi.removeTag(ticket.id, tag.id))}
                    >
                      {tag.name} ×
                    </button>
                  ))}
                </span>
                <CompactSelect
                  className="hd-tag-add"
                  value=""
                  disabled={busy}
                  ariaLabel="Přidat tag"
                  onChange={(next) => { if (next) act(() => helpdeskApi.addTag(ticket.id, next)); }}
                  options={[{ value: '', label: 'Přidat tag…' }, ...allTags.filter((t) => !ticketTagIds.has(t.id)).map((t) => ({ value: t.id, label: t.name }))]}
                />
              </ArcPanel>
              <ArcPanel className="hd-side-tile">
                <span className="hd-tile-label">Od</span>
                <span>{ticket.fromName ?? ticket.fromEmail}<br />{ticket.fromEmail}</span>
              </ArcPanel>
              {isRondoContext && (
                <ArcPanel className="hd-side-tile">
                  <span className="hd-tile-label">Rondo uživatel</span>
                  <RondoLink
                    current={ticket.linkedRondoUserId}
                    disabled={busy}
                    onSet={(id) => act(() => helpdeskApi.linkRondoUser(ticket.id, id))}
                  />
                </ArcPanel>
              )}
              <ArcPanel className="hd-side-tile">
                <span className="hd-tile-label">Vytvořeno</span>
                <span>{fmtDateTime(ticket.createdAt)}</span>
              </ArcPanel>
              <ArcPanel className="hd-side-tile">
                <span className="hd-tile-label">Kontakt</span>
                {ticket.contactMatchStatus === 'matched' ? (
                  <span>
                    <strong>{ticket.contactFullName}</strong>
                    {ticket.fromNameVocative && <><br /><span className="hd-tile-sub">Oslovení: {ticket.fromNameVocative}</span></>}
                    {ticket.contactCompany && <><br />{ticket.contactCompany}</>}
                    {ticket.contactRole && <><br /><span className="hd-tile-sub">{ticket.contactRole}</span></>}
                    {(ticket.contactCardUrl || (ticket.contactMatchType === 'lead' && ticket.contactMatchId)) && (
                      <><br /><a className="hd-tile-link" href={ticket.contactCardUrl ?? `/crm/leady/${ticket.contactMatchId}`}>Otevřít kartu</a></>
                    )}
                  </span>
                ) : ticket.contactMatchStatus === 'not_found' ? (
                  <span>
                    <span className="hd-tile-sub">Kontakt nenalezen</span>
                    {ticket.canAddToCard && (
                      <AddContactToCard
                        key={ticket.id}
                        ticketId={ticket.id}
                        email={ticket.fromEmail}
                        defaultName={ticket.fromName ?? ''}
                        onSaved={() => { void reload(); onChanged?.(); }}
                      />
                    )}
                  </span>
                ) : (
                  <span className="hd-tile-sub">Párování probíhá…</span>
                )}
              </ArcPanel>
            </aside>
          </div>
        )}
      </aside>
    </>
  );
}

function RondoLink({ current, disabled, onSet }: { current: string | null; disabled: boolean; onSet: (id: string | null) => void }) {
  const [q, setQ] = useState('');
  const [hits, setHits] = useState<RondoUserHit[]>([]);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!q.trim()) { setHits([]); return; }
    let alive = true;
    const t = setTimeout(() => {
      searchRondoUsers(q.trim())
        .then((r) => { if (alive) { setHits(r.items); setOpen(true); } })
        .catch(() => { if (alive) setHits([]); });
    }, 250);
    return () => { alive = false; clearTimeout(t); };
  }, [q]);

  if (current) {
    return (
      <span className="hd-rondo-link">
        <span className="hd-pill on">#{current}</span>
        <button type="button" className="hd-btn-link hd-danger" disabled={disabled} onClick={() => onSet(null)}>Zrušit párování</button>
      </span>
    );
  }
  return (
    <span className="hd-rondo-link">
      <input
        className="hd-link-search"
        type="text"
        placeholder="Hledat Rondo uživatele…"
        value={q}
        disabled={disabled}
        onChange={(e) => setQ(e.target.value)}
        onFocus={() => hits.length && setOpen(true)}
      />
      {open && hits.length > 0 && (
        <div className="hd-link-results">
          {hits.map((h) => (
            <button
              key={h.id}
              type="button"
              className="hd-link-result"
              disabled={disabled}
              onClick={() => { setOpen(false); setQ(''); onSet(String(h.id)); }}
            >
              <span className="hd-name">{h.name}{h.nickname ? ` (${h.nickname})` : ''}</span>
              <span className="hd-meta">#{h.id}{h.email ? ` · ${h.email}` : ''}</span>
            </button>
          ))}
        </div>
      )}
    </span>
  );
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  const first = parts[0] ?? '';
  if (parts.length === 0) return '?';
  if (parts.length === 1) return first.slice(0, 2).toUpperCase();
  const last = parts[parts.length - 1] ?? '';
  return ((first[0] ?? '') + (last[0] ?? '')).toUpperCase();
}
function dayKey(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toISOString().slice(0, 10);
}
function dayLabel(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const today = new Date();
  const k = d.toISOString().slice(0, 10);
  if (k === today.toISOString().slice(0, 10)) return 'Dnes';
  const y = new Date(today.getTime() - 86400000).toISOString().slice(0, 10);
  if (k === y) return 'Včera';
  return d.toLocaleDateString('cs-CZ', { day: 'numeric', month: 'long', year: 'numeric' });
}

function Bubble({ m, ticket, busy, onArchive }: { m: HelpdeskMessageDto; ticket: HelpdeskTicketDetailDto; busy: boolean; onArchive: (id: string) => void }) {
  const outgoing = m.messageType === 'external_outbound' || m.messageType === 'ai_draft';
  const internal = m.messageType === 'internal_note' || m.messageType === 'linked_space_note';
  const cls = internal ? 'internal' : outgoing ? 'outgoing' : 'incoming';
  const authorName = m.authorUserName ?? (m.authorType === 'customer' ? (ticket.fromName ?? ticket.fromEmail) : 'AI');
  const avatar = m.authorType === 'ai' ? 'AI' : initials(authorName);
  const time = new Date(m.sentAt);
  const timeStr = Number.isNaN(time.getTime()) ? '' : time.toLocaleTimeString('cs-CZ', { hour: '2-digit', minute: '2-digit' });
  return (
    <div className={`hd-bubble-wrap ${cls}`}>
      <div className="hd-bubble-avatar">{avatar}</div>
      <div className="hd-bubble-col">
        <div className="hd-bubble-meta">
          {internal && <span className="hd-mini-badge">Interní</span>}
          {m.messageType === 'external_outbound' && <span className="hd-mini-badge">Odpověď</span>}
          <span className="hd-bubble-author">{authorName}</span>
          <span className="hd-bubble-time">{timeStr}</span>
          <button
            type="button"
            className="hd-bubble-archive"
            title="Archivovat zprávu"
            disabled={busy}
            onClick={() => { void confirmDialog({ title: 'Archivovat zprávu?', confirmLabel: 'Archivovat' }).then((ok) => { if (ok) onArchive(m.id); }); }}
          >Smazat</button>
        </div>
        <div className="hd-bubble">
          {(() => {
            const html = renderMessageHtml(m.bodyHtml);
            return html
              ? <div className="hd-bubble-body hd-bubble-body--html">{html}</div>
              : <div className="hd-bubble-body">{messageBodyText(m.bodyPlain, m.bodyHtml)}</div>;
          })()}
          {m.attachments && m.attachments.filter((a) => !a.isInline).length > 0 && (
            <div className="hd-bubble-attachments">
              {m.attachments.filter((a) => !a.isInline).map((a) => {
                const risky = isRiskyAttachment(a.filename);
                const href = helpdeskApi.attachmentDownloadUrl(ticket.id, a.id);
                return (
                  <a
                    key={a.id}
                    className={risky ? 'hd-attachment-chip hd-attachment-chip--risky' : 'hd-attachment-chip'}
                    href={href}
                    download={a.filename}
                    title={risky ? `${a.filename} · ${fmtBytes(a.sizeBytes)} · rizikový typ souboru` : `${a.filename} · ${fmtBytes(a.sizeBytes)}`}
                    onClick={risky ? (e) => { e.preventDefault(); void confirmRiskyDownload(href, a.filename); } : undefined}
                  >
                    <span className="hd-attachment-icon" aria-hidden="true">{risky ? '⚠️' : '📎'}</span>
                    <span className="hd-attachment-name">{a.filename}</span>
                    <span className="hd-attachment-size">{fmtBytes(a.sizeBytes)}</span>
                  </a>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function fmtBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} kB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

function Conversation({ ticket, busy, onArchive }: { ticket: HelpdeskTicketDetailDto; busy: boolean; onArchive: (id: string) => void }) {
  const msgs = ticket.messages;
  const older = msgs.length > 0 ? msgs.slice(0, -1).reverse() : [];
  const olderAttachmentCount = older.reduce((n, m) => n + (m.attachments?.filter((a) => !a.isInline).length ?? 0), 0);
  const [showOlder, setShowOlder] = useState(() => msgs.length <= 3 || olderAttachmentCount > 0);
  if (msgs.length === 0) return <div className="hd-conversation"><p className="hd-empty">Žádné zprávy.</p></div>;

  const latest = msgs[msgs.length - 1]!;

  const renderWithDividers = (list: HelpdeskMessageDto[], keyPrefix: string) => {
    const out: JSX.Element[] = [];
    let lastDay = '';
    for (const m of list) {
      const k = dayKey(m.sentAt);
      if (k && k !== lastDay) {
        out.push(<div key={`d-${keyPrefix}-${k}`} className="hd-day-divider">{dayLabel(m.sentAt)}</div>);
        lastDay = k;
      }
      out.push(<Bubble key={m.id} m={m} ticket={ticket} busy={busy} onArchive={onArchive} />);
    }
    return out;
  };

  return (
    <div className="hd-conversation">
      <div className="hd-conversation-latest">{renderWithDividers([latest], 'new')}</div>
      {older.length > 0 && (
        <div className="hd-conversation-older">
          <button type="button" className="hd-older-toggle" onClick={() => setShowOlder((v) => !v)}>
            {showOlder ? '▴' : '▾'} Předchozí zprávy {older.length}
            {olderAttachmentCount > 0 && <> · 📎 {olderAttachmentCount}</>}
          </button>
          {showOlder && <div className="hd-older-content">{renderWithDividers(older, 'old')}</div>}
        </div>
      )}
    </div>
  );
}

async function confirmRiskyDownload(href: string, filename: string): Promise<void> {
  const ok = await confirmDialog({
    title: 'Rizikový typ souboru',
    message: `Soubor ${filename} může obsahovat makra nebo spustitelný kód. Stáhněte ho jen tehdy, když odesílatele znáte a přílohu očekáváte. Neotevírejte ho, pokud si nejste jistí.`,
    confirmLabel: 'Přesto stáhnout',
    danger: true,
  });
  if (!ok) return;
  const link = document.createElement('a');
  link.href = href;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
}
